/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useRetailerStockSearch } from '../../utils/retailer-stock'
import { getChannelSearchTypeKey, profileAdvertisingLocationFromChannel } from '../../utils/channel-utilities'
import {
    FilterWithSelectedOptions,
    updateFiltersFromUrl,
    updateUrl,
    updateUrlFromSelectedFilters,
} from '../../utils/uri.utilities'
import { Facet, Filter } from '../../utils/search/search-results-util/search-results.util'
import { FacetName, FilterName, SearchResultsSort } from '../../__generated__/fed-graphql-types'

import { SelectProps } from '@at/design-system-elements'
import { purgeCache, useCacheRestorer } from '../../graphql/cache'
import { createSelectedFilterMap, Selected, selectedFacetsFrom } from './selected-options'
import { parse, pick } from 'query-string'
import { useFederatedQuery as useQuery } from '@at/sauron-platform-graphql/lib/hooks/query.hook'
import { SearchResultsFacetsWithGroupsQueryWithGroupsGql } from '../../contexts/search-results-context/query/search-results-facets.fed.query'
import { Error as UserLocationError, UserLocationContext } from '../../contexts/user-location-context'
import { facetsByChannelSearchEvolution } from '../../utils/search/channel-facets-util/channel-facets.util'
import { FilterErrorType, isFilterError } from '../../utils/query-utilities'
import { useQueryFilters } from '../../hooks/search-context/use-query-filters'
import { useQuerySortBy } from '../../hooks/search-context/use-query-sort-by'
import { FilterQueryParam, OtherQueryParams } from '../../utils/search/filter-query-param-util/query-params'
import { facetsGroupsByChannel } from '../../utils/search/channel-facets-util/channel-facets-groups-util'
import { QueryResult } from '@apollo/client'
import { useChannel } from '../../hooks/use-abtest.hook'
import { reorderFilterPills, updateSelectedFiltersOrder } from '../../components/pill/pill.util'
import { DeepWriteable } from '../../utils/deep-writeable'
import {
    SearchResultsFacetsWithGroupsQuery_Query,
    SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults,
    SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facetGroups_FacetGroup,
    SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet,
    SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet_filters_Filter,
    SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_filterPills_FilterPill,
    SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_finance_Finance,
    SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_suggestedFacets_SuggestedFacet,
    SearchResultsFacetsWithGroupsQueryVariables,
} from '../../contexts/search-results-context/query/__generated__/search-results-facets.fed.query'
import { Channel } from '@at/sauron-platform-core'
import { setSearchId } from '../../utils/set-search-id'
import { useIsLeasingSearch } from '../../utils/is-leasing-search'
import { defaultChannelGroupFiltersOrder } from '../search-form/default-group-filters-order'
import { useCheckParamsAndOpenModal } from '../../hooks/saved-search/check-params-and-open-modal'
import { useFeatureFlags } from '../../hooks/use-feature-flags'

export type IsLastSelectedType = { facet: FacetName; filter: FilterName } | undefined

export type SearchFormContext = {
    selectedMap: Map<FilterName, Selected>
    isSelected: (filter: FilterName) => boolean
    updateLastSelected: (filter: FilterName) => void
    hasSelected: (filter: FilterName, value: string) => boolean
    isFacetSelected: (facet: FacetName) => boolean
    suggestedFacets: ReadonlyArray<SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_suggestedFacets_SuggestedFacet> | null
    queryFacets: ReadonlyArray<SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet>
    queryFacetGroups: ReadonlyArray<SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facetGroups_FacetGroup | null> | null
    updateSelected: (facetName: FacetName, filters: FilterWithSelectedOptions[], isFilterPill?: boolean) => void
    resetSearch: () => void
    clearSearch: () => void
    sortBySelected: string | undefined
    updateSortBySelected: (sort: string | null, shouldUpdateUrl?: boolean) => void
    sortByDisplayText: string
    sortByDisplayTooltip: string
    isLastSelected: IsLastSelectedType
    filterNumber: number
    sortByOptions: SelectProps['options']
    countFilters: (facetName: FacetName) => number
    countFiltersOptions: (facetName: FacetName) => number
    totalResults: number
    finance?: SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_finance_Finance | null
    loading: boolean
    previousData: SearchResultsFacetsWithGroupsQuery_Query | null
    localFilterPills: SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_filterPills_FilterPill[]
    setLocalFilterPills: Dispatch<
        SetStateAction<SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_filterPills_FilterPill[]>
    >
}

/* eslint-disable @typescript-eslint/no-unused-vars */
export const defaultValue: SearchFormContext = {
    selectedMap: new Map<FilterName, Selected>(),
    isSelected: (filter: FilterName) => false,
    updateLastSelected: (filter: FilterName) => {},
    hasSelected: (filter: FilterName, value: string) => false,
    isFacetSelected: (facet: FacetName) => false,
    queryFacets: new Array<SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet>(),
    queryFacetGroups: null,
    suggestedFacets: null,
    updateSelected: (facetName: FacetName, filters: FilterWithSelectedOptions[], isFilterPill = false) => {},
    resetSearch: () => {},
    clearSearch: () => {},
    sortBySelected: '',
    updateSortBySelected: (sort: string | null, shouldUpdateUrl?: boolean) => {},
    sortByDisplayText: '',
    sortByDisplayTooltip: '',
    isLastSelected: undefined,
    filterNumber: 0,
    sortByOptions: [] as SelectProps['options'],
    countFilters: () => 0,
    countFiltersOptions: () => 0,
    totalResults: 0,
    finance: null,
    loading: false,
    previousData: null,
    localFilterPills: [],
    setLocalFilterPills: () => {},
}
/* eslint-enable @typescript-eslint/no-unused-vars */

export const FormContext = React.createContext(defaultValue)
FormContext.displayName = 'FormContext'

const asUrlFormattedSort = (sortBy: SearchResultsSort | undefined) => sortBy?.toString().replace('_', '-')

function shouldNotBeCounted(facet: Facet, filter: Filter): boolean {
    return (
        filter.isOnlySelected ||
        !filter.selected?.length ||
        facet.facet === FacetName.price_search_type ||
        facet.facet === FacetName.postcode ||
        facet.facet === FacetName.location ||
        facet.facet === FacetName.lat_long ||
        (facet.facet === FacetName.home_delivery_adverts && filter.selected.includes('include')) ||
        facet.facet === FacetName.lease_product_type
    )
}

export function countFilters(queryFacets: Facet[]): number {
    return queryFacets.reduce((count, facet) => {
        return (
            count +
            facet.filters.reduce((fc, filter) => {
                if (shouldNotBeCounted(facet, filter)) {
                    return fc
                }
                return fc + 1
            }, 0)
        )
    }, 0)
}

function useFacetsQuery() {
    const { cacheInitialised } = useCacheRestorer()
    const { error, setError } = useContext(UserLocationContext)
    const channel: Channel = useChannel()
    const filters = useQueryFilters()
    const sortBy = useQuerySortBy()
    const isLeasing = useIsLeasingSearch()
    const channelSearchTypeKey = getChannelSearchTypeKey(channel, isLeasing)
    useCheckParamsAndOpenModal()
    const featureFlags = useFeatureFlags()

    const facets = facetsByChannelSearchEvolution.get(channelSearchTypeKey) ?? []

    const variables: SearchResultsFacetsWithGroupsQueryVariables = {
        filters,
        channel,
        sortBy,
        facets: facets,
        facetGroups: facetsGroupsByChannel.get(channelSearchTypeKey) ?? [],
        useSearchEvoFacets: true,
        useGridLayout: true,
        featureFlags: featureFlags,
    }

    const options = {
        notifyOnNetworkStatusChange: true, // fixes https://autotrader.atlassian.net/browse/PRRE-1741 TODO: review if/when apollo client gets upgraded
        skip: !cacheInitialised || error === UserLocationError.CLIENT,
        ssr: false,
    }

    const gqlQuery = SearchResultsFacetsWithGroupsQueryWithGroupsGql

    const query: QueryResult<SearchResultsFacetsWithGroupsQuery_Query, SearchResultsFacetsWithGroupsQueryVariables> =
        useQuery(gqlQuery, {
            variables,
            ...options,
        })

    /**
     *  Check for errors from BE, specifically errors related to location (postcode, location or latitude and longitude)
     * */
    const [isRetailerStockSearch] = useRetailerStockSearch()

    useEffect(() => {
        const locationError = isFilterError(query.error, FilterErrorType.LOCATION)
        if (locationError) {
            setError(isRetailerStockSearch ? UserLocationError.NONE : UserLocationError.EXTERNAL)
        } else if (error !== UserLocationError.CLIENT) {
            setError(UserLocationError.NONE)
        }
    }, [isRetailerStockSearch, query.error, setError, error, query.loading])

    return {
        ...query,
        // FIXME: "Feature" with useQuery still returning data from previous result when errors are present.
        //  Doesn't follow the expected "none" (default) errorPolicy configuration 😢
        // See https://github.com/apollographql/react-apollo/blob/9e4b573a5ae8ee068bc6f9229e6d84b36974da6b/packages/hooks/src/data/QueryData.ts#L281
        data: query.error ? undefined : query.loading ? query.previousData : query.data,
        // data: query.error ? undefined : query.data,
        loading: !cacheInitialised || query.loading,
        previousData: query.previousData,
        variables,
    }
}

export const Form: React.FC<React.PropsWithChildren<object>> = ({ children }) => {
    const history = useHistory()
    const {
        location,
        location: { search },
    } = history
    const [isRetailerStockSearch, retailerId] = useRetailerStockSearch()
    const { data, variables, loading, previousData } = useFacetsQuery()
    const queryFacets = structuredClone(data?.searchResults?.facets || []) as DeepWriteable<
        SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet[]
    > // Response from GQL
    const queryFacetGroups =
        (data?.searchResults as SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults)?.facetGroups || null // Response from GQL
    const filterPills =
        (data?.searchResults as SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults)?.filterPills || null // Response from GQL
    const suggestedFacets =
        (data?.searchResults as SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults)?.suggestedFacets || null // Response from GQL
    const urlFilters = Array.isArray(variables?.filters) ? variables?.filters : [] // Variables passed to GQL
    updateFiltersFromUrl(location, queryFacets)
    const channel = useChannel()
    const isLeasing = useIsLeasingSearch()
    const channelSearchTypeKey = getChannelSearchTypeKey(channel, isLeasing)
    const [selectedFiltersOrder, setSelectedFiltersOrder] = useState(
        defaultChannelGroupFiltersOrder.get(channelSearchTypeKey) || []
    )
    const [lastSelected, setLastSelected] = useState(defaultValue.isLastSelected)

    const [localFilterPills, setLocalFilterPills] = useState<
        SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_filterPills_FilterPill[]
    >([])

    const [localSuggestedFacets, setLocalSuggestedFacets] = useState<
        SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_suggestedFacets_SuggestedFacet[]
    >([])

    const selectedMap = createSelectedFilterMap(urlFilters, queryFacets as Facet[])
    const selectedFacetMap = selectedFacetsFrom(selectedMap)

    const sortBy = data?.searchResults?.sortBy
    const sortBySelected = asUrlFormattedSort(sortBy?.selected)
    // The current URL is created using whatever sort the user has most recently selected in the UI.
    // The React components need to ensure they only set valid sort values for the search, e.g. no Relevance for
    // finance searches. This field is correct whenever a URL is created inside this context as the value is
    // mutated in the ref whenever a user changes selected sort.
    // The sort returned by the back-end is always used to render the selected sort component so that it is
    // consistent with the actual sort used to perform the search.
    const userSelectedSortBy = useRef<string | null>(null)

    const updateSortBySelected = useCallback(
        (sort: string | null, shouldUpdateUrl = true) => {
            userSelectedSortBy.current = sort

            setSearchId()
            const parsedSearch = parse(search)
            if (shouldUpdateUrl) {
                updateUrl(
                    history,
                    {
                        page: null,
                        sort,
                    },
                    parsedSearch
                )
            }
        },
        [userSelectedSortBy, search, history]
    )

    // Ensure the current user selected sort matches the sort from search results data
    useEffect(() => {
        if (sortBySelected && sortBySelected !== userSelectedSortBy.current) {
            userSelectedSortBy.current = sortBySelected
        }
    }, [sortBySelected, userSelectedSortBy])

    const sortByOptions: SelectProps['options'] =
        sortBy?.options.map((o) => {
            return { displayValue: o.name, value: asUrlFormattedSort(o.value) }
        }) || []

    const sortBySelectedOption = sortBy?.options.filter((o) => {
        return o.value === sortBy?.selected
    })

    const sortByDisplayText = sortBySelectedOption?.[0]?.name ?? ''

    const sortByDisplayTooltip = sortBySelectedOption?.[0]?.descriptionTooltipText ?? ''

    const selectedCount = countFilters(queryFacets as Facet[])

    const filterNumber = selectedCount > 0 ? selectedCount : 0

    useEffect(() => {
        const filter = lastSelected?.filter
        if (filter) {
            setSelectedFiltersOrder((currentValue: FilterName[]) => {
                return updateSelectedFiltersOrder(currentValue, filter)
            })
        }
    }, [lastSelected])

    useEffect(() => {
        if (!loading && suggestedFacets !== null) {
            setLocalSuggestedFacets([...suggestedFacets])
        }
    }, [suggestedFacets, loading])

    useEffect(() => {
        if (!loading && filterPills !== null) {
            setLocalFilterPills([...filterPills])
        }
    }, [filterPills, loading])

    return (
        <FormContext.Provider
            value={{
                ...defaultValue,
                selectedMap,
                queryFacets,
                queryFacetGroups,
                suggestedFacets: localSuggestedFacets,
                sortBySelected,
                updateSortBySelected,
                sortByOptions,
                sortByDisplayText,
                sortByDisplayTooltip,
                filterNumber,
                finance: data?.searchResults?.finance,
                isLastSelected: lastSelected,
                isSelected: (filterName) => !!selectedMap.get(filterName),
                isFacetSelected: (facetName) => selectedFacetMap.has(facetName),
                hasSelected: (filterName, value) => selectedMap.get(filterName)?.values.includes(value) || false,
                updateSelected: (facetName, filters, isFilterPill) => {
                    setSearchId()
                    if (!isFilterPill) {
                        setLastSelected({ facet: facetName, filter: filters[0].filter })
                    }
                    updateUrlFromSelectedFilters(history, filters, userSelectedSortBy.current)
                },
                updateLastSelected: (filterName) => selectedMap.get(filterName),
                resetSearch: () => {
                    purgeCache()
                    const { pathname, search } = history.location
                    let queryString = pick(search, [
                        FilterQueryParam.postcode,
                        FilterQueryParam.lat_long,
                        FilterQueryParam.location,
                    ])
                    if (isRetailerStockSearch) {
                        const sep: string = queryString.startsWith('?') ? '&' : '?'
                        queryString +=
                            sep +
                            'retailerId=' +
                            retailerId +
                            '&advertising-location=' +
                            profileAdvertisingLocationFromChannel(channel)
                    }
                    history.push(pathname + queryString)
                },
                clearSearch: () => {
                    purgeCache()
                    const { pathname, search } = history.location
                    let queryString = pick(search, [
                        FilterQueryParam.postcode,
                        FilterQueryParam.lat_long,
                        FilterQueryParam.location,
                        FilterQueryParam.sort,
                        OtherQueryParams.bucket, // don't clear bucket param for testing feature shielded changes
                    ])
                    if (isRetailerStockSearch) {
                        const sep: string = queryString.startsWith('?') ? '&' : '?'
                        queryString +=
                            sep +
                            'retailerId=' +
                            retailerId +
                            '&advertising-location=' +
                            profileAdvertisingLocationFromChannel(channel)
                    }
                    history.push(pathname + queryString)
                },
                countFilters: (facetName: FacetName) => {
                    const facetFound = queryFacets.find(
                        (facet: SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet) =>
                            facet.facet === facetName
                    )
                    return facetFound ? facetFound.filters[0].options.length : 0
                },
                countFiltersOptions: (facetName: FacetName) => {
                    const facetFound = queryFacets.find(
                        (facet: SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet) =>
                            facet.facet === facetName
                    )

                    return facetFound
                        ? facetFound.filters.reduce(
                              (
                                  acc: number,
                                  filter: SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet_filters_Filter
                              ) => {
                                  return acc + filter.options.length
                              },
                              0
                          )
                        : 0
                },
                totalResults: data?.searchResults?.page?.results?.count || 0,
                loading: loading,
                previousData: previousData || null,
                localFilterPills: reorderFilterPills(localFilterPills, selectedFiltersOrder) || [],
                setLocalFilterPills,
            }}
        >
            {children}
        </FormContext.Provider>
    )
}

export const useForm = () => {
    return React.useContext(FormContext)
}
